import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
} from "chart.js"
import dayjs from "dayjs"

import { baseOptions } from "../../DashboardPage/components/chartsOptions"
import { xLabelIndexes } from "./axis"

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend
)

// Text and surface tokens of the chart (never a series colour for text).
export const TEXT_COLOR = { light: "#27272A", dark: "#FCFCFC" }
export const SURFACE_COLOR = { light: "#FFFFFF", dark: "#141414" }
const TICK_COLOR = "#667085"
const TICK_FONT = { size: 12, weight: 400 }

// An interval's x-axis label: its first day (DAA-2-23), in the platform charts' "Oct 05".
const dateLabel = (isoDate) => dayjs(isoDate).format("MMM DD")

// The hovered interval's date in a tooltip, "MMM D" (DAA-2-25): "Oct 9", not "Oct 09" (CHA1-4518).
export const tooltipDateLabel = (isoDate) => dayjs(isoDate).format("MMM D")

// Both days of the comparison card, with their year: "Oct 9, 2026" beside "Sep 11, 2026".
export const cardDateLabel = (isoDate) => dayjs(isoDate).format("MMM D, YYYY")

// Below the chart at its left, one round marker per series followed by its name (DAA-2-25).
export const legendOptions = ({ show, theme, filter }) => ({
  display: show,
  position: "bottom",
  align: "start",
  labels: {
    usePointStyle: true,
    pointStyle: "circle",
    // chart.js draws a legend circle of boxHeight × √2 across: this gives the design's 8px dot.
    boxWidth: 8 / Math.SQRT2,
    boxHeight: 8 / Math.SQRT2,
    padding: 16,
    color: TEXT_COLOR[theme] ?? TEXT_COLOR.light,
    font: { size: 14, family: "Inter, sans-serif" },
    // A line's legend dot would carry the line's 2px stroke on top; the design's dot is a plain fill.
    generateLabels: (chart) =>
      ChartJS.defaults.plugins.legend.labels
        .generateLabels(chart)
        .map((item) => ({ ...item, lineWidth: 0 })),
    filter
  }
})

// The value axis of every chart: five ticks over `range` (axis.js), the platform's dashed grid.
export const valueAxis = ({ theme, range, format, stacked }) => {
  const base = baseOptions(theme).scales.y

  return {
    ...base,
    stacked,
    beginAtZero: range.min === 0,
    min: range.min,
    max: range.max,
    ticks: {
      ...base.ticks,
      font: TICK_FONT,
      color: TICK_COLOR,
      stepSize: range.step,
      maxTicksLimit: 5,
      callback: (value) => format(value)
    }
  }
}

// A chart over the period's intervals: at most six date labels on the x-axis, one whole step apart
// from the first, fewer on a narrow plot; five ticks on the y-axis; the platform's dark tooltip (`tooltip` is its external
// renderer).
export const intervalChartOptions = ({
  theme,
  labels,
  range,
  yFormat,
  legend,
  legendFilter,
  tooltip,
  stacked
}) => {
  const base = baseOptions(theme)

  return {
    ...base,
    // The Dashboard's base curves its lines (lineTension); this page draws straight segments (DAA-2-25).
    datasets: { line: { pointRadius: 0, tension: 0 } },
    interaction: { mode: "index", intersect: false },
    hover: { mode: "index", intersect: false },
    plugins: {
      title: { display: false },
      legend: legendOptions({ show: legend, theme, filter: legendFilter }),
      tooltip: { enabled: false, mode: "index", intersect: false, external: tooltip }
    },
    scales: {
      x: {
        type: "category",
        stacked,
        grid: { display: false },
        border: { display: false },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          font: TICK_FONT,
          color: TICK_COLOR,
          // `this` is the scale: its width is the plot's when chart.js lays the labels out.
          callback(_value, index) {
            const shownLabels = xLabelIndexes(labels.length, {
              width: this.maxWidth,
              offset: this.options.offset
            })

            return shownLabels.has(index) ? dateLabel(labels[index]) : ""
          }
        }
      },
      y: valueAxis({ theme, range, format: yFormat, stacked })
    }
  }
}
